<script setup lang="ts">
import "@/assets/css/vendors/tabulator.css";
import { ref, onMounted, reactive, watch, computed } from "vue";
import api from "@/utils/axios";
import Button from "@/components/Base/Button";
import Lucide from "@/components/Base/Lucide";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { createIcons, icons } from "lucide";
import { Dialog } from "@/components/Base/Headless";
import Toastify from "toastify-js";
import { useI18n } from "vue-i18n";

import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { stringToHTML } from "@/utils/helper";
import { useNotification } from "@/composables/useNotification";
import PageTitle from "@template/components/PageTitle/PageTitle.vue";

const props = defineProps<{
    mode?: 'loans' | 'documents'
}>();

const { t, locale } = useI18n();
const router = useRouter(); 
const authStore = useAuthStore();
const isManagement = computed(() => ['ADMIN', 'TECNICO'].includes(authStore.user?.role));

const isLoading = ref(true);
const tableRef = ref<HTMLDivElement>();
const tabulator = ref<Tabulator>();
const loans = ref<any[]>([]);

const docTableRef = ref<HTMLDivElement>();
const docTabulator = ref<Tabulator>();
const dailyDocs = ref<any[]>([]);

// Modal Control
const confirmModalOpen = ref(false);
const selectedLoanId = ref<number | null>(null);

// Document Modal detail
const isDocModalOpen = ref(false);
const selectedDocument = ref<any>(null);
const isLoadingDoc = ref(false);
const { error: notifyError } = useNotification();

// Loan Modal detail
const isLoanModalOpen = ref(false);
const selectedLoan = ref<any>(null);

const MAX_VISIBLE_DOCS = 2;

const getEstadoBadgeColor = (estadoFisico: string | null | undefined) => {
    let badgeColor = "bg-slate-100 text-slate-600";
    const estado = (estadoFisico || "").toLowerCase();
    if (estado.includes("buen")) badgeColor = "bg-success/20 text-success border border-success/20";
    else if (estado.includes("regul")) badgeColor = "bg-warning/20 text-warning border border-warning/20";
    else if (estado.includes("mal") || estado.includes("deterior")) badgeColor = "bg-danger/20 text-danger border border-danger/20";
    return badgeColor;
};

const renderDocItemHtml = (doc: any) => {
    const badgeColor = getEstadoBadgeColor(doc.estado_fisico);
    return `
        <div class="text-[11px] leading-tight flex items-start flex-col">
            <button type="button" class="doc-btn text-blue-600 hover:underline text-left font-medium mb-1 transition-colors" data-doc-id="${doc.id}" title="${t('activities.go_to_document')}">
                ${doc.title}
            </button>
            <span class="px-1.5 py-0.5 rounded text-[12px] font-bold">
                ${doc.signatura || 'N/A'}
            </span>
            <span class="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${badgeColor}">
                ${doc.estado_fisico || 'N/A'}
            </span>
        </div>`;
};

const loanStatusLabel = (status: string) => {
    if (status === 'active') return t('loans_status.active');
    if (status === 'requested') return t('loans_status.requested');
    if (status === 'returned') return t('loans_status.returned');
    return status;
};

const loanStatusColor = (status: string) => {
    if (status === 'active') return 'text-success';
    if (status === 'requested') return 'text-warning';
    return 'text-slate-500';
};

const fetchDailyLoans = async () => {
  isLoading.value = true;
  try {
    const res = await api.get("/loans/daily");
    loans.value = res.data.data;
    if (tabulator.value) {
        tabulator.value.replaceData(loans.value);
    }
  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
  }
};

const markEntry = async (id: number) => {
  try {
     await api.put(`/loans/${id}/entry`, {});
     Toastify({ text: t("toasts.entry_success"), style: { background: "#10b981" } }).showToast();
     fetchDailyLoans();
  } catch (e) { console.error(e); }
};

const confirmExit = (id: number) => {
    selectedLoanId.value = id;
    confirmModalOpen.value = true;
};

const performExit = async () => {
    if (!selectedLoanId.value) return;
    try {
        await api.put(`/loans/${selectedLoanId.value}/return`, {});
        Toastify({ text: t("toasts.exit_success"), style: { background: "#10b981" } }).showToast();
        fetchDailyLoans();
    } catch (e) { 
        console.error(e); 
        Toastify({ text: t("toasts.exit_error"), style: { background: "#cc0000" } }).showToast();
    } finally {
        confirmModalOpen.value = false;
        selectedLoanId.value = null;
    }
};

const downloadPdf = async (id: number) => {
    try {
        const response = await api.get(`/loans/${id}/pdf?t=${Date.now()}`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
        console.error(e);
        Toastify({ text: t("toasts.pdf_error"), style: { background: "#cc0000" } }).showToast();
    }
};

const downloadDailyPdf = async () => {
    try {
        const response = await api.get(`/loans/daily/pdf?t=${Date.now()}`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
        console.error(e);
        Toastify({ text: t("toasts.daily_pdf_error"), style: { background: "#cc0000" } }).showToast();
    }
};

const openDocumentModal = async (docId: number) => {
    isLoadingDoc.value = true;
    selectedDocument.value = null;
    isDocModalOpen.value = true;
    try {
        const res = await api.get(`/documents/${docId}`);
        selectedDocument.value = res.data.data;
    } catch (e: any) {
        console.error(e);
        isDocModalOpen.value = false;
        notifyError(t("common.error"), t("toasts.load_doc_error"));
    } finally {
        isLoadingDoc.value = false;
    }
};

const openLoanModal = (loan: any) => {
    selectedLoan.value = loan;
    isLoanModalOpen.value = true;
};

const goToLoanDetail = () => {
    if (!selectedLoan.value) return;
    const id = selectedLoan.value.id;
    isLoanModalOpen.value = false;
    router.push(`/loans/${id}?from=dashboard`);
};

const fetchDailyDocs = async () => {
    try {
        const res = await api.get("/loans/daily-documents");
        dailyDocs.value = res.data;
        if (docTabulator.value) {
            docTabulator.value.replaceData(dailyDocs.value);
        }
    } catch (e) {
        console.error(e);
        notifyError(t("common.error"), t("toasts.load_daily_docs_error"));
    }
};

const getDocColumns = () => [
    {
      title: t('documents.columns.title'),
      field: "title",
      minWidth: 250,
      formatter(cell: any) {
          const data = cell.getData() as any;
          return stringToHTML(`<button type="button" class="doc-btn text-blue-600 hover:underline text-left font-medium" data-doc-id="${data.id}">${data.title}</button>`);
      },
      cellClick: (e: any, cell: any) => {
          const target = e.target as HTMLElement;
          const btn = target.closest('.doc-btn') as HTMLElement;
          if (btn) {
              const docId = parseInt(btn.dataset.docId || '0');
              if (docId) openDocumentModal(docId);
          }
      }
    },
    {
      title: `${t('documents.columns.signatura')} / ${t('documents.columns.fondo')}`,
      field: "signatura",
      formatter(cell: any) {
          const data = cell.getData() as any;
          return `<div><div class="font-medium">${data.signatura || 'N/A'}</div><div class="text-xs text-slate-500">${data.fondo || ''}</div></div>`;
      }
    },
    {
      title: t('dashboard.physical_state'),
      field: "estado_fisico",
      hozAlign: "center" as any,
      formatter(cell: any) {
          const val = cell.getValue() || "N/A";
          let badgeColor = "bg-slate-100 text-slate-600";
          const estado = (val).toLowerCase();
          if (estado.includes("buen")) badgeColor = "bg-success/20 text-success border border-success/20";
          else if (estado.includes("regul")) badgeColor = "bg-warning/20 text-warning border border-warning/20";
          else if (estado.includes("mal") || estado.includes("deterior")) badgeColor = "bg-danger/20 text-danger border border-danger/20";
          return `<span class="px-2 py-0.5 rounded text-[10px] font-bold uppercase ${badgeColor}">${val}</span>`;
      }
    },
    {
      title: t('dashboard.current_user'),
      field: "first_name",
      formatter(cell: any) {
          const data = cell.getData() as any;
          return `<div><div class="font-medium">${data.first_name} ${data.last_name}</div><div class="text-xs text-slate-500">${data.dni}</div></div>`;
      }
    },
    {
        title: t('documents.columns.actions'),
        hozAlign: "center" as any,
        cssClass: "text-sm font-medium uppercase whitespace-nowrap",
        width: 120,
        minWidth: 120,
        headerSort: false,
        formatter() {
            return `<div class="flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium uppercase"><a href="javascript:;" class="inline-flex items-center justify-center text-primary btn-doc-view"><svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg></a></div>`;
        },
        cellClick: (e: any, cell: any) => {
            const data = cell.getData() as any;
            openDocumentModal(data.id);
        }
    }
];

const getLoanColumns = () => [
    {
      title: "ID",
      field: "id",
      width: 70,
      sorter: "number" as any,
      hozAlign: "center" as any,
    },
    {
      title: t('common.user'),
      field: "user.last_name",
      minWidth: 150,
      formatter(cell: any) {
          const data = cell.getData() as any;
          return `<div class="font-medium">${data.user.first_name} ${data.user.last_name}</div><div class="text-xs text-slate-500">${data.user.dni}</div>`;
      }
    },
    {
        title: t('menu.documents'),
        field: "documents",
        minWidth: 220,
        formatter(cell: any) {
            const data = cell.getData() as any;
            const docs = data.documents || [];
            if (docs.length === 0) return "";

            const visibleDocs = docs.slice(0, MAX_VISIBLE_DOCS);
            const hasMore = docs.length > MAX_VISIBLE_DOCS;
            const remaining = docs.length - MAX_VISIBLE_DOCS;

            let html = `<div class="flex flex-col gap-1.5 py-1">`;
            visibleDocs.forEach((doc: any) => {
                html += renderDocItemHtml(doc);
            });

            if (hasMore) {
                const iconPlus = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>`;
                html += `
                    <button type="button"
                        class="btn-more-docs mt-1 inline-flex items-center justify-center gap-1 self-start rounded-md border border-primary/30 bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/20 transition-colors"
                        title="${t('dashboard.more_documents', { count: remaining })}"
                        data-loan-id="${data.id}">
                        ${iconPlus}
                        <span>${remaining}</span>
                    </button>`;
            }

            html += `</div>`;
            return stringToHTML(html);
        },
        cellClick: (e: any, cell: any) => {
            const target = e.target as HTMLElement;
            const moreBtn = target.closest('.btn-more-docs') as HTMLElement;
            if (moreBtn) {
                openLoanModal(cell.getData());
                return;
            }
            const btn = target.closest('.doc-btn') as HTMLElement;
            if (btn) {
                const docId = parseInt(btn.dataset.docId || '0');
                if (docId) openDocumentModal(docId);
            }
        }
    },
    {
        title: t('loans.columns.shift'),
        field: "shift",
        hozAlign: "center" as any,
        formatter(cell: any) {
            const val = cell.getValue();
            const label = val ? t(`loans.shifts.${val}`) : "";
            return `<span class="capitalize">${label}</span>`;
        }
    },
    {
        title: t('documents.columns.status'),
        field: "status",
        hozAlign: "center" as any,
        cssClass: "text-sm font-medium uppercase",
        formatter(cell: any) {
            const val = cell.getValue();
            let color = "text-slate-500";
            let label = val;
            if (val === 'active') { color = "text-success"; label = t('loans_status.active'); }
            else if (val === 'requested') { color = "text-warning"; label = t('loans_status.requested'); }
            else if (val === 'returned') { color = "text-slate-500"; label = t('loans_status.returned'); }
            return `<div class="flex items-center justify-center ${color} font-medium uppercase"><i data-lucide="check-square" class="w-4 h-4 mr-2"></i> ${label}</div>`;
        }
    },
    {
        title: t('documents.columns.actions'),
        hozAlign: "center" as any,
        cssClass: "text-sm font-medium uppercase whitespace-nowrap",
        width: 220,
        minWidth: 220,
        headerSort: false,
        formatter(cell: any) {
            const data = cell.getData() as any;

            const iconEye     = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>`;
            const iconPdf     = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>`;
            const iconLogIn   = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>`;
            const iconLogOut  = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>`;

            let html = `<div class="flex justify-center items-center gap-2 whitespace-nowrap text-sm font-medium uppercase">`;

            html += `<a href="javascript:;" class="text-slate-500 btn-view inline-flex items-center justify-center" title="${t('common.view')}">${iconEye}</a>`;
            html += `<a href="javascript:;" class="text-primary btn-pdf inline-flex items-center justify-center" title="PDF">${iconPdf}</a>`;

            if (isManagement) {
                if (data.status === 'requested') {
                    html += `<a href="javascript:;" class="text-success btn-entry inline-flex items-center gap-1 font-medium text-sm uppercase whitespace-nowrap" title="${t('dashboard.confirm_exit_desc')}">${iconLogIn} ${t('dashboard.btn_entry')}</a>`;
                }
                if (data.status === 'active') {
                    html += `<a href="javascript:;" class="text-danger btn-exit inline-flex items-center gap-1 font-medium text-sm uppercase whitespace-nowrap" title="${t('dashboard.confirm_exit')}">${iconLogOut} ${t('dashboard.btn_exit')}</a>`;
                }
            }

            html += `</div>`;
            return html;
        },
        cellClick: (e: any, cell: any) => {
             const target = e.target as HTMLElement;
             const data = cell.getData() as any;
             
             if (target.closest('.btn-view')) {
                 router.push(`/loans/${data.id}?from=dashboard`);
             } else if (target.closest('.btn-pdf')) {
                 downloadPdf(data.id);
             } else if (target.closest('.btn-entry')) {
                 markEntry(data.id);
             } else if (target.closest('.btn-exit')) {
                 confirmExit(data.id);
             }
        }
    }
];

const initDocTabulator = () => {
  if (docTableRef.value) {
    docTabulator.value = new Tabulator(docTableRef.value, {
      data: dailyDocs.value,
      layout: "fitColumns",
      responsiveLayout: "collapse",
      placeholder: t('dashboard.no_daily_docs'),
      columns: getDocColumns(),
    });

    docTabulator.value.on("renderComplete", () => {
      createIcons({
        icons,
        attrs: { "stroke-width": 1.5 },
        nameAttr: "data-lucide",
      });
    });
  }
};

const initTabulator = () => {
  if (tableRef.value) {
    tabulator.value = new Tabulator(tableRef.value, {
      data: loans.value,
      layout: "fitColumns",
      responsiveLayout: "collapse",
      placeholder: t('dashboard.no_daily_loans'),
      columns: getLoanColumns(),
    });
    
    tabulator.value.on("renderComplete", () => {
      createIcons({
        icons,
        attrs: { "stroke-width": 1.5 },
        nameAttr: "data-lucide",
      });
    });
  }
};

const refresh = () => {
    if (props.mode === 'documents' || !props.mode) {
        if (!docTabulator.value) initDocTabulator();
        fetchDailyDocs();
    }
    
    if (props.mode === 'loans' || !props.mode) {
        if (!tabulator.value) initTabulator();
        fetchDailyLoans();
    }
}

defineExpose({ refresh });

watch(locale, () => {
    if (tabulator.value) tabulator.value.setColumns(getLoanColumns());
    if (docTabulator.value) docTabulator.value.setColumns(getDocColumns());
});

onMounted(async () => {
  if (props.mode === 'loans' || !props.mode) {
      await fetchDailyLoans();
      initTabulator();
  }
  
  if (props.mode === 'documents' || !props.mode) {
      await fetchDailyDocs();
      if (!docTabulator.value) initDocTabulator();
  }
});
</script>

<template>
  <div class="col-span-12">
        <!-- Panel Préstamos -->
        <div v-if="mode === 'loans' || !mode">
            <div class="intro-y flex flex-col gap-4 sm:flex-row sm:items-center mb-5">
                <PageTitle
                  :title="$t('dashboard.daily_loans')"
                  icon="BookKey"
                  variant="emerald"
                  heading="h3"
                />
                <div class="flex flex-wrap items-center sm:ml-auto gap-2">
                    <Button variant="outline-primary" class="mr-2 flex items-center" @click="fetchDailyLoans">
                        <Lucide icon="RefreshCw" class="w-4 h-4 mr-2" /> {{ $t('dashboard.update') }}
                    </Button>
                    <Button v-if="isManagement" variant="primary" class="mr-2 flex items-center" @click="router.push('/loans/create?from=dashboard')">
                        <Lucide icon="Plus" class="w-4 h-4 mr-2" /> {{ $t('loans.new_request') }}
                    </Button>
                    <Button v-if="isManagement" variant="primary" class="flex items-center" @click="downloadDailyPdf">
                        <Lucide icon="Printer" class="w-4 h-4 mr-2" /> {{ $t('dashboard.print_sheets') }}
                    </Button>
                </div>
            </div>
            
            <div class="intro-y box p-5">
                <div class="overflow-x-auto scrollbar-hidden">
                    <div id="tabulator-daily" ref="tableRef"></div>
                </div>
            </div>
        </div>

        <!-- Panel Documentos -->
        <div v-if="mode === 'documents' || !mode" :class="{'mt-10': !mode}">
            <div class="intro-y flex flex-col gap-4 sm:flex-row sm:items-center mb-5">
                <PageTitle
                  :title="`${$t('dashboard.daily_docs')}`"
                  icon="FileText"
                  variant="cyan"
                  heading="h3"
                />
                <div class="flex items-center sm:ml-auto">
                    <Button variant="outline-primary" class="flex items-center" @click="fetchDailyDocs">
                        <Lucide icon="RefreshCw" class="w-4 h-4 mr-2" /> {{ $t('dashboard.update') }}
                    </Button>
                </div>
            </div>
            
            <div class="intro-y box p-5">
                <div class="overflow-x-auto scrollbar-hidden">
                    <div id="tabulator-docs" ref="docTableRef"></div>
                </div>
            </div>
        </div>
    
    <!-- Exit Confirmation Modal (Standard Style) -->
    <Dialog :open="confirmModalOpen" @close="confirmModalOpen = false">
        <Dialog.Panel>
            <div class="p-5 text-center">
                <Lucide icon="LogOut" class="w-16 h-16 mx-auto mt-3 text-warning" /> 
                <div class="mt-5 text-2xl font-bold text-slate-800">{{ $t('dashboard.confirm_exit') }}</div>
                <div class="mt-2 text-sm text-slate-500">
                    {{ $t('dashboard.confirm_exit_desc') }}
                </div>
            </div>
            <div class="px-5 pb-8 text-center">
                <Button type="button" variant="outline-secondary" @click="confirmModalOpen = false" class="w-24 mr-1">
                    {{ $t('common.cancel') }}
                </Button>
                <Button type="button" variant="primary" class="w-24" @click="performExit">
                    {{ $t('common.confirm') }}
                </Button>
            </div>
        </Dialog.Panel>
    </Dialog>

    <!-- Loan Detail Modal -->
    <Dialog :open="isLoanModalOpen" @close="isLoanModalOpen = false" size="xl">
        <Dialog.Panel>
            <Dialog.Title class="flex items-center px-5 py-3 border-b border-slate-200/60 dark:border-darkmode-400">
                <Lucide icon="BookOpen" class="w-5 h-5 mr-2 text-primary" />
                <h2 class="text-2xl font-bold text-slate-800 mr-auto">
                    {{ $t('loans.loan_detail') }}
                    <span v-if="selectedLoan" class="text-base font-medium text-slate-400 ml-2">#{{ selectedLoan.id }}</span>
                </h2>
                <button @click="isLoanModalOpen = false" class="text-slate-400 hover:text-slate-600">
                    <Lucide icon="X" class="w-5 h-5" />
                </button>
            </Dialog.Title>

            <div class="p-5" v-if="selectedLoan">
                <div class="grid grid-cols-12 gap-4">
                    <div class="col-span-12 md:col-span-6 space-y-4">
                        <div>
                            <div class="text-xs text-slate-500 uppercase font-semibold">{{ $t('common.user') }}</div>
                            <div class="font-medium text-slate-700 dark:text-slate-300">
                                {{ selectedLoan.user?.first_name }} {{ selectedLoan.user?.last_name }}
                            </div>
                            <div class="text-xs text-slate-500">{{ selectedLoan.user?.dni }}</div>
                        </div>
                        <div>
                            <div class="text-xs text-slate-500 uppercase font-semibold">{{ $t('loans.columns.consultation_date') }}</div>
                            <div class="font-medium text-slate-600 dark:text-slate-400">{{ selectedLoan.consultation_date || 'N/A' }}</div>
                        </div>
                        <div>
                            <div class="text-xs text-slate-500 uppercase font-semibold">{{ $t('loans.columns.shift') }}</div>
                            <div class="font-medium text-slate-600 dark:text-slate-400 capitalize">
                                {{ selectedLoan.shift ? $t(`loans.shifts.${selectedLoan.shift}`) : 'N/A' }}
                            </div>
                        </div>
                    </div>

                    <div class="col-span-12 md:col-span-6 space-y-4">
                        <div>
                            <div class="text-xs text-slate-500 uppercase font-semibold">{{ $t('loans.columns.reason') }}</div>
                            <div class="font-medium text-slate-600 dark:text-slate-400">{{ selectedLoan.reason?.name || 'N/A' }}</div>
                        </div>
                        <div>
                            <div class="text-xs text-slate-500 uppercase font-semibold">{{ $t('loans.columns.status') }}</div>
                            <div class="mt-1 font-medium uppercase" :class="loanStatusColor(selectedLoan.status)">
                                {{ loanStatusLabel(selectedLoan.status) }}
                            </div>
                        </div>
                        <div class="flex gap-6">
                            <div v-if="selectedLoan.entry_at">
                                <div class="text-xs text-slate-500 uppercase font-semibold">{{ $t('dashboard.entry_at') }}</div>
                                <div class="font-medium text-slate-600 dark:text-slate-400 text-sm">{{ selectedLoan.entry_at }}</div>
                            </div>
                            <div v-if="selectedLoan.returned_at">
                                <div class="text-xs text-slate-500 uppercase font-semibold">{{ $t('dashboard.returned_at') }}</div>
                                <div class="font-medium text-slate-600 dark:text-slate-400 text-sm">{{ selectedLoan.returned_at }}</div>
                            </div>
                        </div>
                    </div>

                    <div v-if="selectedLoan.notes" class="col-span-12 pt-4 border-t border-slate-100 dark:border-darkmode-400">
                        <div class="text-xs text-slate-500 uppercase font-semibold mb-1">{{ $t('documents.form.fields.notes') }}</div>
                        <div class="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-line">{{ selectedLoan.notes }}</div>
                    </div>

                    <div class="col-span-12 pt-4 border-t border-slate-100 dark:border-darkmode-400">
                        <div class="text-xs text-slate-500 uppercase font-semibold mb-3 flex items-center gap-2">
                            {{ $t('menu.documents') }}
                            <span class="bg-slate-100 text-slate-600 rounded-full px-2 py-0.5 text-[10px] font-bold normal-case">
                                {{ (selectedLoan.documents || []).length }}
                            </span>
                        </div>
                        <div v-if="!(selectedLoan.documents || []).length" class="text-sm text-slate-400">
                            —
                        </div>
                        <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <button
                                v-for="doc in selectedLoan.documents"
                                :key="doc.id"
                                type="button"
                                class="text-left p-3 rounded-lg border border-slate-100 dark:border-darkmode-400 hover:border-primary/40 hover:bg-slate-50 dark:hover:bg-darkmode-400 transition-colors"
                                @click="openDocumentModal(doc.id)"
                            >
                                <div class="text-sm font-medium text-blue-600 mb-1">{{ doc.title }}</div>
                                <div class="text-xs text-slate-500 mb-1.5">{{ doc.signatura || 'N/A' }}</div>
                                <span
                                    class="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase"
                                    :class="getEstadoBadgeColor(doc.estado_fisico)"
                                >
                                    {{ doc.estado_fisico || 'N/A' }}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="px-5 py-4 flex justify-end gap-2 border-t border-slate-200/60 dark:border-darkmode-400">
                <Button
                    v-if="selectedLoan"
                    type="button"
                    variant="outline-primary"
                    class="flex items-center"
                    @click="goToLoanDetail"
                >
                    <Lucide icon="ExternalLink" class="w-4 h-4 mr-2" />
                    {{ $t('common.view') }}
                </Button>
                <Button type="button" variant="outline-secondary" @click="isLoanModalOpen = false" class="w-24">
                    {{ $t('common.close') }}
                </Button>
            </div>
        </Dialog.Panel>
    </Dialog>

    <!-- Document Detail Modal -->
    <Dialog :open="isDocModalOpen" @close="isDocModalOpen = false" size="lg">
        <Dialog.Panel>
            <Dialog.Title class="flex items-center px-5 py-3 border-b border-slate-200/60 dark:border-darkmode-400">
                <Lucide icon="FileText" class="w-5 h-5 mr-2 text-primary" />
                <h2 class="text-2xl font-bold text-slate-800 mr-auto">{{ $t('dashboard.doc_details') }}</h2>
                <button @click="isDocModalOpen = false" class="text-slate-400 hover:text-slate-600">
                    <Lucide icon="X" class="w-5 h-5" />
                </button>
            </Dialog.Title>
            
            <div class="p-5">
                <div v-if="isLoadingDoc" class="flex flex-col items-center justify-center py-10">
                    <Lucide icon="Loader2" class="w-10 h-10 animate-spin text-primary" />
                    <span class="mt-2 text-slate-500">{{ $t('dashboard.loading_details') }}</span>
                </div>
                
                <div v-else-if="selectedDocument" class="grid grid-cols-12 gap-4">
                    <!-- Title Section -->
                    <div class="col-span-12 bg-slate-50 dark:bg-darkmode-400 p-4 rounded-lg border border-slate-100 dark:border-darkmode-400 mb-2">
                        <div class="text-xs text-slate-500 uppercase font-semibold mb-1">{{ $t('documents.columns.title') }}</div>
                        <div class="text-lg font-bold text-slate-700 dark:text-slate-300">{{ selectedDocument.title }}</div>
                    </div>

                    <!-- Main Info Grid -->
                    <div class="col-span-12 md:col-span-6 space-y-4">
                        <div>
                            <div class="text-xs text-slate-500 uppercase font-semibold">{{ $t('documents.columns.signatura') }}</div>
                            <div class="font-medium text-slate-600 dark:text-slate-400">{{ selectedDocument.signatura || 'N/A' }}</div>
                        </div>
                        <div>
                            <div class="text-xs text-slate-500 uppercase font-semibold">{{ $t('documents.columns.fondo') }} / {{ $t('documents.columns.seccion') }}</div>
                            <div class="font-medium text-slate-600 dark:text-slate-400">
                                {{ selectedDocument.fondo || 'N/A' }} 
                                <span v-if="selectedDocument.seccion" class="text-slate-400 mx-1">/</span>
                                {{ selectedDocument.seccion }}
                            </div>
                        </div>
                        <div>
                            <div class="text-xs text-slate-500 uppercase font-semibold">{{ $t('documents.columns.productor') }}</div>
                            <div class="font-medium text-slate-600 dark:text-slate-400">{{ selectedDocument.productor || 'N/A' }}</div>
                        </div>
                    </div>

                    <div class="col-span-12 md:col-span-6 space-y-4">
                        <div>
                            <div class="text-xs text-slate-500 uppercase font-semibold">{{ $t('documents.form.tabs.dates') }}</div>
                            <div class="font-medium text-slate-600 dark:text-slate-400">
                                {{ selectedDocument.date_start || '?' }} - {{ selectedDocument.date_end || '?' }}
                            </div>
                        </div>
                        <div>
                            <div class="text-xs text-slate-500 uppercase font-semibold">{{ $t('documents.form.fields.description_level') }}</div>
                            <div class="font-medium text-slate-600 dark:text-slate-400">{{ selectedDocument.description_level || 'N/A' }}</div>
                        </div>
                        <div>
                            <div class="text-xs text-slate-500 uppercase font-semibold">{{ $t('dashboard.physical_state') }}</div>
                            <div class="mt-1">
                                <span :class="[
                                    'px-2 py-1 rounded text-[10px] font-bold uppercase border',
                                    (selectedDocument.physical_features || '').toLowerCase().includes('buen') ? 'bg-success/10 text-success border-success/20' :
                                    (selectedDocument.physical_features || '').toLowerCase().includes('regul') ? 'bg-warning/10 text-warning border-warning/20' :
                                    'bg-danger/10 text-danger border-danger/20'
                                ]">
                                    {{ selectedDocument.physical_features || $t('reproductions.form.not_applicable') }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Notes / Description -->
                    <div v-if="selectedDocument.description" class="col-span-12 pt-4 border-t border-slate-100 dark:border-darkmode-400">
                        <div class="text-xs text-slate-500 uppercase font-semibold mb-1">{{ $t('documents.columns.description') }}</div>
                        <div class="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-line">{{ selectedDocument.description }}</div>
                    </div>

                    <!-- Digital Status -->
                    <div class="col-span-12 flex gap-4 pt-4 border-t border-slate-100 dark:border-darkmode-400">
                        <div class="flex items-center text-xs" :class="selectedDocument.is_digitized ? 'text-success' : 'text-slate-400'">
                            <Lucide :icon="selectedDocument.is_digitized ? 'CheckCircle' : 'Circle'" class="w-4 h-4 mr-1" />
                            {{ $t('documents.columns.is_digitized') }}
                        </div>
                        <div class="flex items-center text-xs" :class="selectedDocument.is_loanable ? 'text-success' : 'text-danger'">
                            <Lucide :icon="selectedDocument.is_loanable ? 'Unlock' : 'Lock'" class="w-4 h-4 mr-1" />
                            {{ selectedDocument.is_loanable ? $t('documents.form.fields.is_loanable') : 'No ' + $t('documents.form.fields.is_loanable') }}
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="px-5 py-4 text-right border-t border-slate-200/60 dark:border-darkmode-400">
                <Button type="button" variant="outline-secondary" @click="isDocModalOpen = false" class="w-24">
                    {{ $t('common.close') }}
                </Button>
            </div>
        </Dialog.Panel>
    </Dialog>
  </div>
</template>
